import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Banknote, Check, Loader2, Play, FlaskConical, Landmark } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { AdminGrowthSettings } from "../../components/growth/GrowthBits";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const day = (s) => new Date(s.length === 10 ? `${s}T12:00:00` : s).toLocaleDateString();
const KIND = { market: "Sale", attire: "Attire", deposit: "Deposit", registry: "Gift" };

function AdminPayouts() {
  const [d, setD] = useState({ rows: [], test_mode: false });
  const [force, setForce] = useState(false);
  const load = () => api.get("/admin/payouts").then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  async function mark(r) { if (!window.confirm(`Mark $${r.amount} to ${r.name || r.email} as paid by hand?`)) return; try { await api.post("/admin/payouts/mark", { kind: r.kind, id: r.id, note: "manual" }); load(); toast.success("Marked paid"); } catch (e) { toast.error(formatApiError(e)); } }
  async function run() { try { const { data } = await api.post("/admin/payouts/run", { force }); toast.success(`${data.test_mode ? "Test mode · " : ""}Sent ${data.sent}, failed ${data.failed}`); load(); } catch (e) { toast.error(formatApiError(e)); } }
  return (
    <div className="bg-white border border-[var(--line)] rounded-2xl p-5 mt-8" data-testid="admin-payouts">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <p className="font-serif text-2xl">All payouts owed {d.test_mode && <span className="text-[10px] align-middle uppercase tracking-wider bg-amber-100 text-amber-800 rounded-full px-2 py-0.5 font-sans" data-testid="admin-payouts-test-badge">Test mode</span>}</p>
        <div className="flex items-center gap-3">
          {d.test_mode && <label className="text-xs flex items-center gap-1.5 text-[var(--muted)]"><input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} data-testid="admin-payouts-force" /> Skip {5}-day hold (test only)</label>}
          <button onClick={run} className="btn-gold !py-2 text-sm flex items-center gap-1" data-testid="admin-payouts-run"><Play className="w-4 h-4" /> Run auto payouts</button>
        </div>
      </div>
      {d.rows.map((r) => (
        <div key={r.kind + r.id} className="flex items-center gap-3 py-2 border-t border-[var(--line)] text-sm" data-testid={`admin-payout-${r.id}`}>
          <span className="flex-1 truncate">{r.name || r.email} · {r.title}</span>
          <span className="text-xs text-[var(--muted)]">{r.auto ? "auto" : "manual"} · {day(r.release_at)}</span>
          {r.payout_error && <span className="text-xs text-red-600 max-w-[160px] truncate" title={r.payout_error}>{r.payout_error}</span>}
          <span className="font-medium">{money(r.amount)}</span>
          <button onClick={() => mark(r)} className="text-xs text-[var(--gold)]" data-testid={`admin-payout-mark-${r.id}`}>Mark paid</button>
        </div>
      ))}
      {!d.rows.length && <p className="text-sm text-[var(--muted)]">Nothing owed right now.</p>}
    </div>
  );
}

function TestBank({ d, onChange }) {
  const [f, setF] = useState({ holder: "", routing: "110000000", account: "000123456789" });
  const [busy, setBusy] = useState(false);
  async function save(e) {
    e.preventDefault(); setBusy(true);
    try { await api.post("/payouts/connect/test-bank", f); toast.success("Test bank connected. Automatic payouts are on."); onChange(); }
    catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  }
  async function remove() { try { await api.delete("/payouts/connect/test-bank"); toast.success("Test bank removed"); onChange(); } catch (err) { toast.error(formatApiError(err)); } }
  if (d.payouts_enabled && d.bank) return (
    <div className="space-y-1" data-testid="payouts-bank-on">
      <p className="text-sm flex items-center gap-2 text-green-700"><Check className="w-4 h-4" /> Automatic payouts are on</p>
      <p className="text-xs text-[var(--muted)] flex items-center gap-1.5"><Landmark className="w-3.5 h-3.5" /> {d.bank.bank_name} •••{d.bank.last4} · {d.bank.holder}</p>
      <button onClick={remove} className="text-xs text-[var(--muted)] underline" data-testid="payouts-bank-remove">Remove</button>
    </div>
  );
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value.trim() });
  return (
    <form onSubmit={save} className="space-y-2" data-testid="payouts-test-bank-form">
      <input required value={f.holder} onChange={(e) => setF({ ...f, holder: e.target.value })} placeholder="Account holder name" className="input-luxe text-sm py-1.5" data-testid="payouts-bank-holder" />
      <div className="flex gap-2">
        <input required value={f.routing} onChange={set("routing")} inputMode="numeric" maxLength={9} placeholder="Routing" className="input-luxe text-sm py-1.5 w-1/2" data-testid="payouts-bank-routing" />
        <input required value={f.account} onChange={set("account")} inputMode="numeric" maxLength={17} placeholder="Account" className="input-luxe text-sm py-1.5 w-1/2" data-testid="payouts-bank-account" />
      </div>
      <button disabled={busy} className="btn-gold !py-2 text-sm w-full flex items-center justify-center gap-2" data-testid="payouts-bank-save"><Banknote className="w-4 h-4" /> {busy ? "Saving…" : "Connect test bank"}</button>
    </form>
  );
}

function LiveConnect({ d }) {
  const [busy, setBusy] = useState(false);
  async function connect() {
    setBusy(true);
    try { const { data } = await api.post("/payouts/connect/onboard", { origin_url: window.location.origin }); window.location.href = data.url; }
    catch (e) { toast.error(formatApiError(e)); setBusy(false); }
  }
  if (d.payouts_enabled) return <p className="text-sm flex items-center gap-2 text-green-700"><Check className="w-4 h-4" /> Automatic bank payouts are on</p>;
  return (<><p className="text-sm text-[var(--muted)]">{d.connected ? "Finish your bank details with Stripe to turn on automatic payouts." : "Connect a bank account to get paid automatically."}</p>
    <button onClick={connect} disabled={busy} className="btn-gold !py-2 text-sm flex items-center justify-center gap-2" data-testid="payouts-connect-btn"><Banknote className="w-4 h-4" /> {busy ? "Opening Stripe…" : d.connected ? "Finish setup" : "Connect bank"}</button></>);
}

function Ledger({ entries }) {
  return (
    <div className="bg-white border border-[var(--line)] rounded-2xl divide-y divide-[var(--line)]" data-testid="payouts-ledger">
      {entries.map((e) => (
        <div key={e.kind + e.id} className="flex items-center gap-3 px-5 py-3 text-sm" data-testid={`payout-row-${e.id}`}>
          <span className="text-[10px] uppercase tracking-wider font-semibold text-[#8a6d2f] w-14 shrink-0">{KIND[e.kind] || e.kind}</span>
          <span className="flex-1 min-w-0"><span className="block truncate">{e.title}</span>
            <span className="block text-xs text-[var(--muted)]" data-testid={`payout-status-${e.id}`}>{e.payout_status !== "paid" ? `Releases ${day(e.release_at)}` : e.payout_method === "manual" ? "Paid by our team" : `Sent ${day(e.payout_at)}${e.arrives_by ? ` · arrives by ${day(e.arrives_by)}` : ""}${e.payout_method === "stripe_test" ? " · test" : ""}`}</span></span>
          <span className="font-medium">{money(e.amount)}</span>
        </div>
      ))}
      {!entries.length && <p className="px-5 py-10 text-center text-[var(--muted)] text-sm">No payments yet. Attire payments, card gifts, deposits and marketplace sales show up here.</p>}
    </div>
  );
}

export default function PayoutsPage() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [params] = useSearchParams();
  const load = () => api.get("/payouts/me").then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); if (params.get("connect") === "done") toast.success("Bank details saved with Stripe"); }, [params]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!d) return <Loader2 className="w-6 h-6 animate-spin text-[var(--gold)]" />;
  return (
    <div data-testid="payouts-page">
      <p className="font-script text-[var(--gold)] text-3xl">Payouts</p>
      <h1 className="font-serif font-light text-4xl mb-6">Your money</h1>
      {d.test_mode && <p className="mb-4 text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded-xl px-4 py-2.5 flex items-center gap-2" data-testid="payouts-test-note"><FlaskConical className="w-4 h-4 shrink-0" /> {d.test_note} Use routing 110000000 and account 000123456789.</p>}
      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-[var(--bg-warm)] rounded-2xl p-5"><p className="font-serif text-3xl" data-testid="payouts-pending">{money(d.pending)}</p><p className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">On the way</p></div>
        <div className="bg-[var(--ink)] text-white rounded-2xl p-5"><p className="font-serif text-3xl text-[var(--gold)]" data-testid="payouts-paid">{money(d.paid)}</p><p className="text-[11px] uppercase tracking-[0.16em] text-white/60">Paid to you</p></div>
        <div className="bg-white border border-[var(--line)] rounded-2xl p-5 flex flex-col justify-center gap-2" data-testid="payouts-connect">
          {d.test_mode ? <TestBank d={d} onChange={load} /> : <LiveConnect d={d} />}
        </div>
      </div>
      <p className="text-xs text-[var(--muted)] mb-3">Card payments are held for {d.hold_days} days in case of refunds, then sent to your bank automatically. It usually arrives in 5–7 days. Without a connected bank, our team pays you by hand.</p>
      <Ledger entries={d.entries} />
      {user?.role === "admin" && <><AdminPayouts /><AdminGrowthSettings /></>}
    </div>
  );
}
